/**
 * useSpeurtochts Hook
 * Hook for fetching and managing speurtochts list
 */

import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  Speurtocht,
  SpeurttochtWithSession,
  SpeurttochtSession,
  ActiveSessionData,
} from "../types";

interface UseSpeurtochtsReturn {
  /** All speurtochts for the active weekend */
  speurtochts: SpeurttochtWithSession[];
  /** Active session for user's team (for dashboard card) */
  activeSession: ActiveSessionData | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Refetch speurtochts */
  refetch: () => Promise<void>;
  /** Refetch speurtochts (alias) */
  refresh: () => Promise<void>;
  /** Get a single speurtocht by ID */
  getSpeurtocht: (id: string) => Promise<Speurtocht | null>;
}

interface UseSpeurtochtsOptions {
  /** Auto-fetch on mount */
  autoFetch?: boolean;
  /** Include draft speurtochts (admin only) */
  includeDrafts?: boolean;
}

/**
 * Hook for fetching speurtochts list
 */
export const useSpeurtochts = (
  options: UseSpeurtochtsOptions = {}
): UseSpeurtochtsReturn => {
  const { autoFetch = true, includeDrafts = false } = options;
  const { activeWeekend, userTeam, activeProfile, roles } = useAuth();
  const { isFamilieAdmin, isSuperAdmin } = roles;

  const [speurtochts, setSpeurtochts] = useState<SpeurttochtWithSession[]>([]);
  const [activeSession, setActiveSession] = useState<ActiveSessionData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all speurtochts for the active weekend
   */
  const fetchSpeurtochts = useCallback(async () => {
    if (!activeWeekend?.id) {
      setSpeurtochts([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Build query - include steps count via subquery
      let query = supabase
        .from("speurtochts")
        .select(
          `
          *,
          creator:created_by(id, full_name, avatar_url),
          speurtocht_steps(count)
        `
        )
        .eq("weekend_id", activeWeekend.id)
        .order("created_at", { ascending: false });

      // Filter by status unless admin wants drafts
      if (!includeDrafts || (!isFamilieAdmin && !isSuperAdmin)) {
        query = query.in("status", ["active", "completed"]);
      }

      const { data: speurtochtsData, error: speurtochtsError } = await query;

      if (speurtochtsError) {
        throw new Error(speurtochtsError.message);
      }

      // Fetch sessions for user's team if they have one
      let sessions: SpeurttochtSession[] = [];
      if (userTeam?.id) {
        const { data: sessionsData, error: sessionsError } = await supabase
          .from("speurtocht_sessions")
          .select("*")
          .eq("team_id", userTeam.id);

        if (sessionsError) {
          console.error("Error fetching sessions:", sessionsError);
        } else {
          sessions = sessionsData as SpeurttochtSession[];
        }
      }

      // Combine speurtochts with session data and steps count
      const speurtochtsWithSessions: SpeurttochtWithSession[] = (
        speurtochtsData as (Speurtocht & { speurtocht_steps: { count: number }[] })[]
      ).map((speurtocht) => {
        const session = sessions.find(
          (s) => s.speurtocht_id === speurtocht.id
        );
        const now = new Date();

        // Extract steps count from the nested query result
        const steps_count = speurtocht.speurtocht_steps?.[0]?.count ?? 0;

        // Determine if user can start
        let can_start = true;
        let start_reason:
          | "already_started"
          | "not_available_yet"
          | "ended"
          | "no_team"
          | undefined;

        if (!userTeam?.id) {
          can_start = false;
          start_reason = "no_team";
        } else if (session) {
          can_start = false;
          start_reason = "already_started";
        } else if (
          speurtocht.available_from &&
          new Date(speurtocht.available_from) > now
        ) {
          can_start = false;
          start_reason = "not_available_yet";
        } else if (
          speurtocht.available_until &&
          new Date(speurtocht.available_until) < now
        ) {
          can_start = false;
          start_reason = "ended";
        }

        // Remove the nested speurtocht_steps from the result
        const { speurtocht_steps: _, ...speurttochtWithoutSteps } = speurtocht;

        return {
          ...speurttochtWithoutSteps,
          steps_count,
          session,
          can_start,
          start_reason,
        };
      });

      setSpeurtochts(speurtochtsWithSessions);

      // Check for active session (for dashboard card)
      const activeSessionData = sessions.find((s) => s.status === "active");
      if (activeSessionData) {
        const speurtocht = speurtochtsData?.find(
          (sp) => sp.id === activeSessionData.speurtocht_id
        ) as Speurtocht | undefined;

        if (speurtocht) {
          setActiveSession({
            session_id: activeSessionData.id,
            speurtocht_id: activeSessionData.speurtocht_id,
            speurtocht_name: speurtocht.name,
            speurtocht_icon: speurtocht.icon,
            speurtocht_color: speurtocht.color,
            current_step_index: activeSessionData.current_step_index,
            total_steps: activeSessionData.total_steps,
            completed_steps: activeSessionData.completed_steps,
            started_at: activeSessionData.started_at,
            expires_at: activeSessionData.expires_at,
            total_points: activeSessionData.total_points,
          });
        }
      } else {
        setActiveSession(null);
      }
    } catch (err) {
      console.error("Error fetching speurtochts:", err);
      setError(
        err instanceof Error ? err.message : "Fout bij laden speurtochts"
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    activeWeekend?.id,
    userTeam?.id,
    includeDrafts,
    isFamilieAdmin,
    isSuperAdmin,
  ]);

  /**
   * Get a single speurtocht by ID
   */
  const getSpeurtocht = useCallback(
    async (id: string): Promise<Speurtocht | null> => {
      const { data, error: fetchError } = await supabase
        .from("speurtochts")
        .select(
          `
          *,
          creator:created_by(id, full_name, avatar_url)
        `
        )
        .eq("id", id)
        .single();

      if (fetchError) {
        console.error("Error fetching speurtocht:", fetchError);
        return null;
      }

      return data as Speurtocht;
    },
    []
  );

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && activeWeekend?.id) {
      fetchSpeurtochts();
    }
  }, [autoFetch, activeWeekend?.id, fetchSpeurtochts]);

  return {
    speurtochts,
    activeSession,
    isLoading,
    error,
    refetch: fetchSpeurtochts,
    refresh: fetchSpeurtochts, // Alias for convenience
    getSpeurtocht,
  };
};

